import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import BigSpinner from '../General/BigSpinner';
import { toast } from '../../utilities/appToast';
import {
    createSidebarBannerApi,
    deleteSidebarBannerApi,
    getSidebarBannersApi,
    reorderSidebarBannersApi,
    updateSidebarBannerApi,
    uploadSidebarBannerImageApi,
} from '../../api/axios';
import './ListBanners.css';

const EMPTY_FORM = {
    id: null,
    idContacto: 1,
    urlImagen: '',
    titulo: '',
    textoModal: '',
    contactType: 'whatsapp',
    telefonoWhatsapp: '',
    mensajeWhatsapp: '',
    email: '',
    asuntoEmail: '',
    cuerpoEmail: '',
    textoBoton: 'Consultar',
    visible: true,
    orden: 0,
};

const getApiMessage = (error, fallback) => {
    const data = error?.response?.data || error;
    const firstError = Array.isArray(data?.errors) ? data.errors[0] : null;

    return data?.mensaje
        || firstError?.message
        || firstError?.Message
        || error?.message
        || fallback;
};

const throwIfApiHasErrors = (data) => {
    if (data?.hasErrors) {
        const error = new Error('La API devolvió errores de validación.');
        error.response = { data };
        throw error;
    }

    return data;
};

const asText = (value) => (typeof value === 'string' ? value : '');
const normalizeImageUrl = (value) => asText(value).replace('/api/Banners/archivo/', '/api/SidebarBanners/archivo/');

const ListBanners = () => {
    const userType = useSelector((state) => state.general?.userType);
    const canManage = Boolean(userType?.isAdminMod || userType?.isCategory);

    const [banners, setBanners] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [bannerToDelete, setBannerToDelete] = useState(null);

    const isEdit = Boolean(form.id);

    const loadBanners = async () => {
        setIsLoading(true);
        try {
            const data = await getSidebarBannersApi();
            setBanners(Array.isArray(data?.banners)
                ? data.banners.map((banner) => ({
                    ...banner,
                    urlImagen: normalizeImageUrl(banner.urlImagen),
                }))
                : []);
        } catch (error) {
            toast.error(getApiMessage(error, 'No se pudieron cargar los banners.'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (canManage) {
            loadBanners();
        } else {
            setIsLoading(false);
        }
    }, [canManage]);

    useEffect(() => () => {
        if (imagePreview && imagePreview.startsWith('blob:')) {
            URL.revokeObjectURL(imagePreview);
        }
    }, [imagePreview]);

    const openCreate = () => {
        setForm({ ...EMPTY_FORM });
        setImageFile(null);
        setImagePreview('');
        setShowForm(true);
    };

    const openEdit = (banner) => {
        setForm({
            ...EMPTY_FORM,
            ...banner,
            urlImagen: normalizeImageUrl(banner.urlImagen),
            titulo: asText(banner.titulo),
            textoModal: asText(banner.textoModal),
            telefonoWhatsapp: asText(banner.telefonoWhatsapp),
            mensajeWhatsapp: asText(banner.mensajeWhatsapp),
            email: asText(banner.email),
            asuntoEmail: asText(banner.asuntoEmail),
            cuerpoEmail: asText(banner.cuerpoEmail),
            textoBoton: asText(banner.textoBoton) || 'Consultar',
            contactType: banner.idContacto === 2 ? 'email' : 'whatsapp',
            visible: banner.visible !== false,
        });
        setImageFile(null);
        setImagePreview(normalizeImageUrl(banner.urlImagen));
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setImageFile(null);
        setForm({ ...EMPTY_FORM });
        setImagePreview('');
    };

    const changeField = (event) => {
        const { name, type, value, checked } = event.target;
        setForm((current) => ({
            ...current,
            ...(name === 'idContacto' ? { contactType: value === '2' ? 'email' : 'whatsapp' } : {}),
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const changeImage = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (!file.type.includes('image')) {
            toast.warn('Seleccione un archivo de imagen.');
            return;
        }

        if (imagePreview.startsWith('blob:')) {
            URL.revokeObjectURL(imagePreview);
        }

        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const validateForm = () => {
        if (!asText(form.titulo).trim()) {
            toast.warn('El título del banner es obligatorio.');
            return false;
        }

        if (!asText(form.urlImagen).trim() && !imageFile) {
            toast.warn('Seleccione una imagen para el banner.');
            return false;
        }

        if (form.contactType === 'email') {
            if (!asText(form.email).trim() || !asText(form.email).includes('@')) {
                toast.warn('Indique un correo electrónico válido.');
                return false;
            }
        } else if (!asText(form.telefonoWhatsapp).trim()) {
            toast.warn('Indique el número de WhatsApp.');
            return false;
        }

        return true;
    };

    const saveBanner = async (event) => {
        event.preventDefault();
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);
        try {
            let imageUrl = asText(form.urlImagen).trim();
            if (imageFile) {
                const uploaded = await uploadSidebarBannerImageApi(imageFile);
                imageUrl = uploaded?.url || '';
            }

            if (!imageUrl) {
                throw new Error('No se pudo obtener la URL de la imagen.');
            }

            const payload = {
                urlImagen: imageUrl,
                titulo: asText(form.titulo).trim(),
                textoModal: asText(form.textoModal).trim(),
                textoBoton: asText(form.textoBoton).trim() || 'Consultar',
                idContacto: Number(form.idContacto),
                telefonoWhatsapp: asText(form.telefonoWhatsapp).trim() || null,
                mensajeWhatsapp: asText(form.mensajeWhatsapp).trim() || null,
                email: asText(form.email).trim() || null,
                asuntoEmail: asText(form.asuntoEmail).trim() || null,
                cuerpoEmail: asText(form.cuerpoEmail).trim() || null,
                visible: form.visible,
                orden: Number(form.orden) || 0,
            };

            if (isEdit) {
                const response = await updateSidebarBannerApi(form.id, payload);
                throwIfApiHasErrors(response);
                toast.success('Banner actualizado.');
            } else {
                const response = await createSidebarBannerApi(payload);
                throwIfApiHasErrors(response);
                toast.success('Banner creado.');
            }

            closeForm();
            await loadBanners();
            window.dispatchEvent(new Event('sidebar-banners-updated'));
        } catch (error) {
            toast.error(getApiMessage(error, 'No se pudo guardar el banner.'));
        } finally {
            setIsSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!bannerToDelete?.id) {
            return;
        }

        setIsSaving(true);
        try {
            await deleteSidebarBannerApi(bannerToDelete.id);
            toast.success('Banner eliminado.');
            setBannerToDelete(null);
            await loadBanners();
            window.dispatchEvent(new Event('sidebar-banners-updated'));
        } catch (error) {
            toast.error(getApiMessage(error, 'No se pudo eliminar el banner.'));
        } finally {
            setIsSaving(false);
        }
    };

    const moveBanner = async (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= banners.length) {
            return;
        }

        const next = [...banners];
        const [moved] = next.splice(index, 1);
        next.splice(targetIndex, 0, moved);

        setBanners(next);
        try {
            const data = await reorderSidebarBannersApi(next.map((banner) => banner.id));
            setBanners(Array.isArray(data?.banners) ? data.banners : next);
            window.dispatchEvent(new Event('sidebar-banners-updated'));
        } catch (error) {
            toast.error(getApiMessage(error, 'No se pudo reordenar el banner.'));
            await loadBanners();
        }
    };

    const contactLabel = useMemo(() => ({
        whatsapp: 'WhatsApp',
        email: 'Correo',
    }), []);

    if (!canManage) {
        return (
            <div className="container">
                <div className="alert alert-warning mt-4">No tenés permisos para administrar los banners del menú.</div>
            </div>
        );
    }

    return (
        <div className="container">
            <div
                className="row mb-3 mb-md-4 align-items-center py-3 px-2 rounded admin-page-heading"
                style={{ background: 'linear-gradient(to right, #f8f9fa 0%, #fff 100%)', borderLeft: '4px solid var(--rojo-megatone, #c00)' }}
            >
                <div className="col-12 col-md-7">
                    <h4 className="mb-0 text-themecolor color-rojo-megatone fw-bold">Banners del menú</h4>
                    <p className="small text-body-secondary mb-0 mt-1">
                        Agregá, editá el texto y elegí si el botón Consultar abre WhatsApp o un correo.
                    </p>
                </div>
                <div className="col-12 col-md-5 text-md-end mt-2 mt-md-0">
                    <button
                        type="button"
                        className="btn btn-sm background-rojo-megatone text-white"
                        onClick={openCreate}
                    >
                        <i className="fa fa-plus-circle mx-1" aria-hidden="true" />
                        Agregar banner
                    </button>
                </div>
            </div>

            {isLoading ? (
                <BigSpinner message="Cargando banners..." />
            ) : banners.length === 0 ? (
                <div className="admin-list-card p-4 p-md-5 text-center text-body-secondary">
                    <h6 className="mb-2">No hay banners configurados</h6>
                    <p className="mb-3">Creá el primero para mostrarlo en el menú lateral.</p>
                    <button type="button" className="btn btn-sm background-rojo-megatone text-white" onClick={openCreate}>
                        Agregar primer banner
                    </button>
                </div>
            ) : (
                banners.map((banner, index) => (
                    <div key={banner.id} className={`admin-list-card sidebar-banner-admin-card p-3 mb-3 ${banner.visible ? 'admin-list-card--vigente' : 'admin-list-card--inactivo'}`}>
                        <div className="d-flex gap-3 align-items-start">
                            <div className="sidebar-banner-admin-preview">
                                {banner.urlImagen ? (
                                    <img src={banner.urlImagen} alt={banner.titulo} />
                                ) : (
                                    <div className="sidebar-banner-admin-preview--empty">
                                        <i className="fa fa-image" aria-hidden="true" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-grow-1 min-w-0">
                                <div className="d-flex flex-wrap justify-content-between gap-2">
                                    <div>
                                        <h6 className="admin-list-card__title mb-1">{banner.titulo}</h6>
                                        <p className="small text-body-secondary mb-2">
                                            {banner.textoModal || 'Sin texto de consulta'}
                                        </p>
                                        <div className="d-flex flex-wrap gap-2">
                                            <span className="badge rounded-pill bg-light text-dark border">
                                                {contactLabel[banner.contactType] || 'WhatsApp'}
                                            </span>
                                            <span className={`badge rounded-pill ${banner.visible ? 'bg-success' : 'bg-secondary'}`}>
                                                {banner.visible ? 'Visible' : 'Oculto'}
                                            </span>
                                            <span className="badge rounded-pill bg-light text-body-secondary border">
                                                {banner.textoBoton || 'Consultar'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => moveBanner(index, -1)}
                                            disabled={index === 0}
                                            aria-label="Subir banner"
                                        >
                                            <i className="fa fa-arrow-up" aria-hidden="true" />
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => moveBanner(index, 1)}
                                            disabled={index === banners.length - 1}
                                            aria-label="Bajar banner"
                                        >
                                            <i className="fa fa-arrow-down" aria-hidden="true" />
                                        </button>
                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openEdit(banner)}>
                                            Editar
                                        </button>
                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setBannerToDelete(banner)}>
                                            Eliminar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))
            )}

            <Modal
                show={showForm}
                onHide={closeForm}
                centered
                size="lg"
                className="sidebar-banner-form-modal"
                backdropClassName="sidebar-banner-form-modal-backdrop"
            >
                <form onSubmit={saveBanner}>
                    <Modal.Header closeButton>
                        <Modal.Title>{isEdit ? 'Editar banner' : 'Agregar banner'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="row g-3">
                            <div className="col-12 col-lg-5">
                                <label className="form-label fw-semibold">Imagen</label>
                                <input
                                    id="banner-image-file"
                                    type="file"
                                    accept="image/*"
                                    className="d-none"
                                    onChange={changeImage}
                                />
                                <label htmlFor="banner-image-file" className="mt-3 d-block text-center" style={{ cursor: 'pointer' }}>
                                    {imagePreview ? (
                                        <img src={imagePreview} alt="Vista previa" className="sidebar-banner-admin-form-preview" />
                                    ) : (
                                        <div className="sidebar-banner-admin-preview sidebar-banner-admin-preview--empty mx-auto">
                                            <i className="fa fa-image" aria-hidden="true" />
                                        </div>
                                    )}
                                </label>
                            </div>
                            <div className="col-12 col-lg-7">
                                <div className="mb-3">
                                    <label className="form-label fw-semibold" htmlFor="banner-contacto">Id de contacto</label>
                                    <input
                                        id="banner-contacto"
                                        name="idContacto"
                                        type="number"
                                        min="1"
                                        className="form-control"
                                        value={form.idContacto}
                                        onChange={changeField}
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold" htmlFor="banner-alt">Título</label>
                                    <input
                                        id="banner-alt"
                                        name="titulo"
                                        className="form-control"
                                        value={form.titulo}
                                        onChange={changeField}
                                        placeholder="Ej: Electro Fans"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold" htmlFor="banner-modal-text">Texto del recuadro</label>
                                    <textarea
                                        id="banner-modal-text"
                                        name="textoModal"
                                        className="form-control"
                                        rows={3}
                                        value={form.textoModal}
                                        onChange={changeField}
                                        placeholder="Texto que ve la persona al abrir el banner"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold" htmlFor="banner-button-text">Texto del botón</label>
                                    <input
                                        id="banner-button-text"
                                        name="textoBoton"
                                        className="form-control"
                                        value={form.textoBoton}
                                        onChange={changeField}
                                        placeholder="Consultar"
                                    />
                                </div>
                                <div className="form-check form-switch">
                                    <input
                                        id="banner-activo"
                                        name="visible"
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={form.visible}
                                        onChange={changeField}
                                    />
                                    <label className="form-check-label" htmlFor="banner-activo">Mostrar en el menú</label>
                                </div>
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">¿A dónde lleva Consultar?</label>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className={`sidebar-banner-contact-option d-block ${form.contactType === 'whatsapp' ? 'is-active' : ''}`}>
                                            <input
                                                type="radio"
                                                name="idContacto"
                                                value="1"
                                                checked={form.contactType === 'whatsapp'}
                                                onChange={changeField}
                                            />
                                            WhatsApp
                                            <div className="small text-body-secondary mt-1">Abre un chat con el mensaje listo.</div>
                                        </label>
                                    </div>
                                    <div className="col-md-6">
                                        <label className={`sidebar-banner-contact-option d-block ${form.contactType === 'email' ? 'is-active' : ''}`}>
                                            <input
                                                type="radio"
                                                name="idContacto"
                                                value="2"
                                                checked={form.contactType === 'email'}
                                                onChange={changeField}
                                            />
                                            Correo electrónico
                                            <div className="small text-body-secondary mt-1">Abre el mail con asunto y texto.</div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {form.contactType === 'email' ? (
                                <>
                                    <div className="col-md-6">
                                        <label className="form-label" htmlFor="banner-email">Correo</label>
                                        <input
                                            id="banner-email"
                                            name="email"
                                            type="email"
                                            className="form-control"
                                            value={form.email}
                                            onChange={changeField}
                                            placeholder="asesor@empresa.com"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" htmlFor="banner-email-subject">Asunto</label>
                                        <input
                                            id="banner-email-subject"
                                            name="asuntoEmail"
                                            className="form-control"
                                            value={form.asuntoEmail}
                                            onChange={changeField}
                                            placeholder="Consulta por banner"
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label" htmlFor="banner-email-body">Mensaje</label>
                                        <textarea
                                            id="banner-email-body"
                                            name="cuerpoEmail"
                                            className="form-control"
                                            rows={3}
                                            value={form.cuerpoEmail}
                                            onChange={changeField}
                                            placeholder="Hola, vi el banner y quiero más información."
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="col-md-5">
                                        <label className="form-label" htmlFor="banner-whatsapp-phone">WhatsApp</label>
                                        <input
                                            id="banner-whatsapp-phone"
                                            name="telefonoWhatsapp"
                                            className="form-control"
                                            value={form.telefonoWhatsapp}
                                            onChange={changeField}
                                            placeholder="5493492587720"
                                        />
                                    </div>
                                    <div className="col-md-7">
                                        <label className="form-label" htmlFor="banner-whatsapp-message">Mensaje prearmado</label>
                                        <textarea
                                            id="banner-whatsapp-message"
                                            name="mensajeWhatsapp"
                                            className="form-control"
                                            rows={3}
                                            value={form.mensajeWhatsapp}
                                            onChange={changeField}
                                            placeholder="Hola, vi la publicación en Marketplace y quiero recibir más información."
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" type="button" onClick={closeForm} disabled={isSaving}>
                            Cancelar
                        </Button>
                        <Button className="background-rojo-megatone text-white" type="submit" disabled={isSaving}>
                            {isSaving ? 'Guardando...' : 'Guardar'}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal>

            <Modal show={Boolean(bannerToDelete)} onHide={() => setBannerToDelete(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Eliminar banner</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    ¿Eliminar <strong>{bannerToDelete?.alt}</strong>? Dejará de rotar en el menú lateral.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setBannerToDelete(null)} disabled={isSaving}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={confirmDelete} disabled={isSaving}>
                        {isSaving ? 'Eliminando...' : 'Eliminar'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ListBanners;
